import React, { useRef, useState } from 'react';
import { pricingService } from '../../_services/pricing.service';
import { setUsage } from './usageStore';
import { autoSpendState, canManageSpend, clampCap, stepCap, spendCopy, spendErrorText, CAP_MIN_PENCE, CAP_MAX_PENCE } from './spendRules';
import { formatPrice } from './money';
import { hapticError, hapticSuccess, hapticTick } from '../../_utils/haptics';
import toast from '../shared/Toast';
import './planBilling.css';

/**
 * Spend protection inside Message balance: the paused card, a held
 * settlement, the limit reached, and "Automatic charges this month" with
 * its meter and Adjust limit. Draws nothing for a balance the API sends no
 * protection for (an older backend).
 */

export function SpendNotices({ usage, isOwner, onCheckActivity }) {
    const s = autoSpendState(usage);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (!s) return null;
    const may = canManageSpend(usage, { isOwner });
    const resume = async () => {
        setBusy(true); setError('');
        try {
            const next = await pricingService.autoResume();
            if (next) setUsage(next);
            hapticSuccess();
            toast.success('Automatic top-ups are back on');
            setConfirming(false);
        } catch (e) { hapticError(); setError(spendErrorText(e)); }
        setBusy(false);
    };
    return (
        <>
            {s.paused && (
                <div className="pb-paused" role="status">
                    <b>Automatic top-ups are paused</b>
                    <p>{spendCopy.paused}</p>
                    {confirming ? (
                        <div className="pb-confirm" role="group" aria-label="Resume automatic top-ups">
                            <p>{spendCopy.resumeConfirm(s)}</p>
                            {error && <p className="pb-error" role="alert">{error}</p>}
                            <div className="pb-paused-actions">
                                <button type="button" className="pb-btn is-secondary" disabled={busy} onClick={() => { setConfirming(false); setError(''); }}>Not yet</button>
                                <button type="button" className="pb-btn is-primary" disabled={busy} onClick={resume}>{busy ? 'Resuming…' : 'Resume'}</button>
                            </div>
                        </div>
                    ) : (
                        <div className="pb-paused-actions">
                            <button type="button" className="pb-btn is-secondary" onClick={() => { hapticTick(); onCheckActivity(); }}>Check activity</button>
                            {may && <button type="button" className="pb-btn is-primary" onClick={() => { hapticTick(); setConfirming(true); }}>Resume automatic top-ups</button>}
                        </div>
                    )}
                </div>
            )}
            {s.held && <div className="pb-note" role="status">{spendCopy.held(s)}</div>}
            {s.reached && s.capPence != null && <div className="pb-note is-warm" role="status">{spendCopy.reached(s)}</div>}
        </>
    );
}

export function SpendLimitRow({ usage, isOwner }) {
    const s = autoSpendState(usage);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const busyRef = useRef(false);
    if (!s || !s.hasCap) return null;
    const may = canManageSpend(usage, { isOwner });
    const m = s.meter;
    const value = draft == null ? s.capPence : draft;
    const save = async () => {
        if (busyRef.current) return;
        busyRef.current = true; setBusy(true); setError('');
        try {
            const next = await pricingService.setAutoSpendCap(clampCap(value));
            if (next) setUsage(next);
            hapticSuccess();
            setEditing(false); setDraft(null);
        } catch (e) { hapticError(); setError(spendErrorText(e)); }
        busyRef.current = false; setBusy(false);
    };
    return (
        <div className={`pb-spend is-${m.level}`}>
            <div className="pb-spend-top">
                <div>
                    <b>{spendCopy.capLine(s)}</b>
                    <span>{spendCopy.promise}</span>
                </div>
                {may && !editing && <button type="button" className="pb-link" onClick={() => { hapticTick(); setEditing(true); setDraft(s.capPence); }}>Adjust limit</button>}
            </div>
            <div className="pb-bar" role="progressbar" aria-label="Automatic charges this month" aria-valuemin={0} aria-valuemax={100} aria-valuenow={m.pct} aria-valuetext={spendCopy.capSr(s)}>
                <span style={{ width: `${Math.max(m.pct, s.spentPence > 0 ? 3 : 0)}%` }} />
            </div>
            {s.alertLevel >= 80 && !s.reached && <p className="pb-meter-resets">{spendCopy.nearly(s)}</p>}
            {editing && (
                <div className="pb-stepper-wrap">
                    <div className="pb-stepper" role="group" aria-label="Monthly limit on automatic charges">
                        <button type="button" aria-label="Lower the limit" disabled={busy || value <= CAP_MIN_PENCE} onClick={() => { hapticTick(); setDraft(stepCap(value, -1)); }}>−</button>
                        <output aria-live="polite">{formatPrice(value)}<small> a month</small></output>
                        <button type="button" aria-label="Raise the limit" disabled={busy || value >= CAP_MAX_PENCE} onClick={() => { hapticTick(); setDraft(stepCap(value, 1)); }}>+</button>
                    </div>
                    <span className="pb-meter-resets">Between {formatPrice(CAP_MIN_PENCE)} and {formatPrice(CAP_MAX_PENCE)}.</span>
                    {error && <p className="pb-error" role="alert">{error}</p>}
                    <div className="pb-paused-actions">
                        <button type="button" className="pb-btn is-secondary" disabled={busy} onClick={() => { setEditing(false); setDraft(null); setError(''); }}>Cancel</button>
                        <button type="button" className="pb-btn is-primary" disabled={busy || value === s.capPence} onClick={save}>{busy ? 'Saving…' : 'Save limit'}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
